import { Button, InputAdornment, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import SearchIcon from '@mui/icons-material/Search'
import JelledSwitch from 'components/JelledSwitch'
import { useActiveOrganization, useFetchActiveOrganization } from 'hooks/use-organization'
import { useEffect, useState } from 'react'
import { OnboardingStep, OrganizationKind, Twin, TwinShareNew, TwinShareVisibility, TwinUpdate } from 'api/model'
import { twinsShare, twinsUpdate } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { myTwinAtom } from 'state/my-twin'
import { twinsAtom } from 'state/twins'
import ProfilePhoto from 'components/ProfilePhoto'
import { capitalizeOnlyFirstLetter } from 'utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import FtuxTooltip from 'components/FtuxTooltip'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import Selector from 'components/Selector'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { useTheme } from '@emotion/react'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { COLORS } from 'app-constants'

export const AnyoneWithTheLinkToggle = ({
  isChecked,
  setIsChecked,
  organizationKind,
}: {
  isChecked: boolean
  setIsChecked: (value: boolean) => void
  organizationKind: string
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const myTwin = useRecoilValue(myTwinAtom)

  const onAnyoneWithTheLinkToggleChange = async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsChecked(checked)

    if (!activeOrganization || !myTwin) {
      return
    }

    try {
      const update: TwinUpdate = {
        name: myTwin.name, // TODO: maybe this isn't required
        share_visibility: checked ? TwinShareVisibility.public : TwinShareVisibility.organization,
      }
      await twinsUpdate(activeOrganization, myTwin.id, update)

      setToast({
        severity: 'success',
        title: `Successfully updated twin sharing settings`,
      })
    } catch (error) {
      setToast({
        severity: 'error',
        title: `Failed to update twin sharing settings`,
      })
    }
  }

  return (
    <Stack
      width="fit-content"
      borderRadius="16px"
      py={1}
      sx={{
        backgroundColor: organizationKind === OrganizationKind.personal ? 'transparent' : COLORS.GRAY_8,
      }}>
      <Stack borderRadius="16px" direction="row" alignItems="center">
        <Stack
          justifyContent="center"
          alignItems="center"
          height="50px"
          width="50px"
          flex="0 0 50px"
          borderRadius="100px"
          sx={{
            backgroundColor: COLORS.JELLED_MID_BLUE_10_PERCENT,
            mr: 2,
          }}>
          <LinkOutlinedIcon
            sx={{
              color: COLORS.JELLED_PRIMARY_BLUE,
              height: '24px',
              width: '24px',
            }}
          />
        </Stack>

        <Stack mr="43px">
          <Typography
            variant="h5.5"
            sx={{
              color: COLORS.GRAY_0,
            }}>
            Anyone with the link
          </Typography>
          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_3,
            }}>
            Your twin is open to public interaction
          </Typography>
        </Stack>

        <JelledSwitch label="" checked={isChecked} onChange={onAnyoneWithTheLinkToggleChange} />
      </Stack>
    </Stack>
  )
}

const CopyLinkButton = ({
  twinShareVisibility,
  updateOnboardingStep,
}: {
  twinShareVisibility: TwinShareVisibility
  updateOnboardingStep: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const user = useRecoilValue(authenticatedUserAtom)
  const setToast = useSetRecoilState(toastAtom)
  const myTwin = useRecoilValue(myTwinAtom)
  const isMobile = useCheckMobileScreen()

  const userHasCompletedShare = !!user?.onboarding_steps_completed.includes(OnboardingStep.twin_share_url_generated)
  const [isFtuxTooltipOpen, setIsFtuxTooltipOpen] = useState(!userHasCompletedShare)
  useEffect(() => {
    if (user?.onboarding_steps_completed.includes(OnboardingStep.twin_share_url_generated)) {
      setIsFtuxTooltipOpen(false)
    } else {
      setIsFtuxTooltipOpen(true)
    }
  }, [user?.onboarding_steps_completed])

  const onCopyLinkClick = async () => {
    if (!activeOrganization || !myTwin) {
      return
    }

    try {
      const twinShareNew: TwinShareNew = {
        visibility: twinShareVisibility,
      }
      const response = await twinsShare(activeOrganization, myTwin.id, twinShareNew)

      const hasCompletedShare =
        user?.onboarding_steps_completed.includes(OnboardingStep.twin_share_url_generated) ||
        user?.onboarding_steps_completed.includes(OnboardingStep.twin_ftux_tour_completed)
      if (!hasCompletedShare) {
        updateOnboardingStep()
      }

      // copy the response link to the clipboard
      navigator.clipboard.writeText(response.data.link)
      setToast({
        severity: 'success',
        title: `Successfully copied share link for twin to clipboard`,
      })
    } catch (error) {
      setToast({
        severity: 'error',
        title: `Failed to generate shared link for twin`,
      })
    }
  }

  return (
    <FtuxTooltip
      open={isFtuxTooltipOpen}
      setOpen={setIsFtuxTooltipOpen}
      headerText="Share your twin"
      subtitleText="Share your twin with other users to help answer their questions, even if you’re away."
      placement={isMobile ? 'bottom' : 'right'}
      onDismiss={() => {
        setIsFtuxTooltipOpen(false)
        updateOnboardingStep()
      }}>
      <Button
        size="medium"
        variant="contained"
        color="primary"
        onClick={onCopyLinkClick}
        startIcon={
          <ContentCopyOutlinedIcon
            sx={{
              color: COLORS.PURE_WHITE,
              height: '16px',
              width: '16px',
            }}
          />
        }
        sx={{
          color: COLORS.PURE_WHITE,
          ml: 2,
          width: 'fit-content',
        }}>
        Copy
      </Button>
    </FtuxTooltip>
  )
}

export const CopyLinkWithOnboardingButton = withOnboardingStep(CopyLinkButton, OnboardingStep.twin_share_url_generated)

const CompanySharingSettings = ({
  isChecked,
  setIsChecked,
  organizationKind,
}: {
  isChecked: boolean
  setIsChecked: (value: boolean) => void
  organizationKind: string
}) => {
  return (
    <Stack mb="4px">
      <Stack mb={2}>
        <AnyoneWithTheLinkToggle
          isChecked={isChecked}
          setIsChecked={setIsChecked}
          organizationKind={organizationKind}
        />
      </Stack>
    </Stack>
  )
}

const TwinShareVisibilitySelector = ({
  twinShareVisibility,
  setTwinShareVisibility,
}: {
  twinShareVisibility: TwinShareVisibility | string
  setTwinShareVisibility: (value: TwinShareVisibility) => void
}) => {
  const twinShareMap = {
    [TwinShareVisibility.public]: 'External',
    [TwinShareVisibility.organization]: 'Company',
  }

  // loop through the twin share visibility types and format them via the twinShareMap
  const formatTwinShareVisibility = (twinShareVisibility: TwinShareVisibility) => {
    return twinShareMap[twinShareVisibility]
  }

  let twinShareVisibilityTypes = [
    'All twins',
    ...Object.values(TwinShareVisibility).map((visibility) => formatTwinShareVisibility(visibility)),
  ]

  // sort the types by alphabetical order, but make sure "All twins" is always first
  twinShareVisibilityTypes = twinShareVisibilityTypes.sort((a, b) => {
    if (a === 'All twins') {
      return -1
    }
    if (b === 'All twins') {
      return 1
    }
    return a.localeCompare(b)
  })

  return (
    <Selector
      id="twin-share-visibility-selector"
      option={twinShareVisibility}
      options={twinShareVisibilityTypes}
      setOption={setTwinShareVisibility}
      isDarkBackdrop={false}
      width="140px"
    />
  )
}

const IndividualSharingSettings = ({
  isChecked,
  setIsChecked,
  organizationKind,
}: {
  isChecked: boolean
  setIsChecked: (value: boolean) => void
  organizationKind: string
}) => {
  return (
    <Stack spacing={2}>
      <AnyoneWithTheLinkToggle isChecked={isChecked} setIsChecked={setIsChecked} organizationKind={organizationKind} />
    </Stack>
  )
}

const SharedTwinRow = ({ organizationKind, twin }: { organizationKind: string; twin: Twin }) => {
  const navigate = useNavigate()

  const onSharedTwinRowClick = () => {
    navigate(`/twin-profile/${twin.id}`)
  }

  return (
    <Stack
      borderRadius="10px"
      direction="row"
      alignItems="center"
      px={2}
      py={1}
      justifyContent="space-between"
      onClick={onSharedTwinRowClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: COLORS.PURE_WHITE,
        '&:hover': {
          backgroundColor: COLORS.GRAY_6,
        },
      }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ProfilePhoto src={twin.picture || twin.user.picture} size="50px" />

        <Stack mr={3}>
          <Typography variant="h5">{twin.name}</Typography>

          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            {twin.public_summary}
          </Typography>
        </Stack>
      </Stack>

      {organizationKind === OrganizationKind.company && twin.twin_share?.visibility && (
        <Typography variant="h5">{capitalizeOnlyFirstLetter(twin.twin_share?.visibility)}</Typography>
      )}
    </Stack>
  )
}

const TwinsSharedWithMe = ({ organizationKind }: { organizationKind: string }) => {
  const [activeOrganization] = useActiveOrganization()
  const twinsSharedWithMe = useRecoilValue(twinsAtom).filter((t) => t.organization_id !== activeOrganization)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const [twinShareVisibility, setTwinShareVisibility] = useState<TwinShareVisibility | string>('All twins')

  // filter twins shared with me based on search text
  const filteredBySearchText = twinsSharedWithMe.filter((t) => {
    if (searchText === '') {
      return true
    }

    return t.name.toLowerCase().includes(searchText.toLowerCase())
  })

  // filter twins based on twin share visibility
  const filteredByTwinShareVisibility = filteredBySearchText.filter((t) => {
    if (twinShareVisibility === 'All twins') {
      return true
    }

    return t.twin_share?.visibility === twinShareVisibility
  })

  // we should only display the selector if the organization is a company
  const isOrganizationKindCompany = organizationKind === OrganizationKind.company

  return (
    <Stack maxWidth="680px">
      <Stack direction="row" alignItems="flex-start" spacing={2} my={3}>
        <TextField
          hiddenLabel
          placeholder="Search twins"
          value={searchText}
          onChange={onSearchTextChange}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    marginRight: '-10px',
                    color: COLORS.GRAY_3,
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '400px',
            input: {
              color: 'secondary.main',
            },
            '& ::placeholder': {
              color: 'secondary.main',
            },
          }}
        />

        {isOrganizationKindCompany && (
          <TwinShareVisibilitySelector
            twinShareVisibility={twinShareVisibility}
            setTwinShareVisibility={setTwinShareVisibility}
          />
        )}
      </Stack>

      <Stack spacing="11px">
        {filteredByTwinShareVisibility.length === 0 ? (
          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_3,
            }}>
            No twins found
          </Typography>
        ) : (
          filteredByTwinShareVisibility.map((t) => {
            return <SharedTwinRow key={t.id} twin={t} organizationKind={organizationKind} />
          })
        )}
      </Stack>
    </Stack>
  )
}

const MyTwinSharing = () => {
  const isMobile = useCheckMobileScreen()
  const theme = useTheme()
  const { organization } = useFetchActiveOrganization()
  const myTwin = useRecoilValue(myTwinAtom)
  const shareLink = myTwin?.twin_share?.link ?? ''

  const [isChecked, setIsChecked] = useState<boolean>(false)
  useEffect(() => {
    if (myTwin?.twin_share?.visibility === TwinShareVisibility.public) {
      setIsChecked(true)
    } else {
      setIsChecked(false)
    }
  }, [myTwin?.twin_share?.visibility])

  const [searchParams] = useSearchParams()
  const isTwinDirectory = searchParams.get('isTwinDirectory') === 'true'

  // if we are in the twin directory, we should default to the "Twins shared with me" tab
  const [tabValue, setTabValue] = useState<number>(isTwinDirectory ? 1 : 0)
  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  if (!organization) {
    return null
  }

  const organizationKind = organization.organization_kind

  return (
    <Stack
      width="100%"
      p={3}
      sx={{
        height: '100vh',
        borderLeft: '1px solid #ddd',
        background: COLORS.GRAY_8,
      }}>
      {!isMobile && (
        <Typography
          variant="h4"
          sx={{
            marginBottom: '24px',
          }}>
          Sharing
        </Typography>
      )}

      <Tabs orientation="horizontal" value={tabValue} onChange={onTabChange} textColor="secondary">
        <Tab
          label="Sharing my twin"
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />
        <Tab
          label="Twins shared with me"
          sx={{
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />
      </Tabs>

      {tabValue === 0 && (
        <Stack>
          <Typography
            my={3}
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Since this account is part of an organization, your twin is by default shared with all members of your
            organization.
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} my={3}>
            <TextFieldWithDarkMode
              isDarkBackdrop={true}
              className="has-label"
              theme={theme}
              label="Twin sharing link"
              value={shareLink}
              onChange={() => {}}
              disabled={true}
              sx={{
                width: '400px',
              }}
            />
            <CopyLinkWithOnboardingButton
              twinShareVisibility={isChecked ? TwinShareVisibility.public : TwinShareVisibility.organization}
            />
          </Stack>

          {organizationKind === 'company' ? (
            <CompanySharingSettings
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              organizationKind={organizationKind}
            />
          ) : (
            <IndividualSharingSettings
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              organizationKind={organizationKind}
            />
          )}
        </Stack>
      )}

      {tabValue === 1 && (
        <Stack>
          <TwinsSharedWithMe organizationKind={organizationKind} />
        </Stack>
      )}
    </Stack>
  )
}

export default MyTwinSharing
